import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import type { Feature, FeatureCollection } from 'geojson';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { FloatingPanelComponent } from '../floating-panel/floating-panel.component';
import { FeatureTableComponent } from './feature-table.component';
import { FeaturePropertiesEditorComponent } from '../feature-properties-editor/feature-properties-editor.component';
import { PanelRotateControlComponent } from '../panel-rotate-control/panel-rotate-control.component';
import { LOCKED_COLLECTION_PROPERTY_KEYS } from '../../shared/model/feature-properties.constants';
import { LayerFeaturePanelService } from './layer-feature-panel.service';
import { MapInteractionService } from '../../ai-components/chat/services/map-interaction.service';
import { GeoService } from '../../viewer-components/services/internal-services/geo.service';
import type { FeatureActionEvent } from './feature-table.types';

/**
 * Floating panel shell for the feature table.
 * Place this component inside a `position: relative` container (e.g. `.map-container`).
 * Visibility is driven by LayerFeaturePanelService. Fitting to the layer's extent is manual
 * (header button) — rotating between layers never moves the map on its own.
 */
@Component({
  selector: 'lib-layer-feature-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FloatingPanelComponent,
    FeatureTableComponent,
    FeaturePropertiesEditorComponent,
    PanelRotateControlComponent,
    MatIconButton,
    MatIcon,
    MatTooltip,
  ],
  template: `
    @if (panelService.activeLayer(); as layer) {
      <lib-floating-panel [title]="layer.layerName" [subtitle]="subtitle()" (closed)="panelService.close()">
        <div panelHeaderActions class="d-flex align-items-center gap-1">
          @if (layer.getAllFeatures) {
            <button matIconButton (click)="onFitToExtent()" aria-label="Fit to extent" matTooltip="Fit to extent">
              <mat-icon>center_focus_strong</mat-icon>
            </button>
          }
          <lib-panel-rotate-control
            [index]="panelService.state()?.currentIndex ?? 0"
            [count]="panelService.state()?.layers?.length ?? 0"
            (previous)="panelService.previous()"
            (next)="panelService.next()"
          />
        </div>
        <div class="d-flex flex-column gap-3 h-100 overflow-auto p-3">
          @if (layer.collectionProperties; as collectionProperties) {
            <lib-feature-properties-editor
              [properties]="collectionProperties"
              [editable]="!!layer.updateCollectionProperties"
              [lockedKeys]="lockedCollectionKeys"
              (save)="layer.updateCollectionProperties?.($event)"
            />
          }
          <lib-feature-table [layer]="layer" (featureAction)="onFeatureAction($event)" />
        </div>
      </lib-floating-panel>
    }
  `,
})
export class LayerFeaturePanelComponent {
  protected readonly panelService = inject(LayerFeaturePanelService);
  protected readonly lockedCollectionKeys = LOCKED_COLLECTION_PROPERTY_KEYS;
  private readonly mapInteraction = inject(MapInteractionService);

  protected readonly subtitle = computed(() => {
    const layer = this.panelService.activeLayer();
    if (!layer) return '';
    const parts = [layer.layerType, layer.totalFeatures != null ? `${layer.totalFeatures} elementos` : null];
    return parts.filter(Boolean).join(' · ');
  });

  protected onFeatureAction(event: FeatureActionEvent): void {
    if (event.action === 'zoom_to_feature') {
      this.zoomToFeature(event.feature);
    }
  }

  protected onFitToExtent(): void {
    const layer = this.panelService.activeLayer();
    const features = layer?.getAllFeatures?.();
    if (!features?.length) return;
    try {
      const collection = { type: 'FeatureCollection', features } as FeatureCollection;
      const wgs84Collection = layer?.sourceEpsg === 3857 ? GeoService.toWgs84(collection, false) : collection;
      const [minX, minY, maxX, maxY] = GeoService.bbox(wgs84Collection);
      this.mapInteraction.zoomToExtent(minX, minY, maxX, maxY);
    } catch (err) {
      console.error('LayerFeaturePanelComponent: failed to compute layer extent', err);
    }
  }

  private zoomToFeature(feature: Feature): void {
    if (!feature.geometry) {
      console.warn('LayerFeaturePanelComponent: zoom_to_feature on feature with no geometry');
      return;
    }
    try {
      // User layers are in EPSG:3857; original GeoJSON files are in EPSG:4326.
      // zoomToExtent always expects WGS84 lon/lat, so convert if needed.
      const sourceEpsg = this.panelService.activeLayer()?.sourceEpsg ?? 4326;
      const wgs84Feature = sourceEpsg === 3857 ? GeoService.toWgs84(feature, false) : feature;
      const [minX, minY, maxX, maxY] = GeoService.bbox(wgs84Feature.geometry);
      this.mapInteraction.zoomToExtent(minX, minY, maxX, maxY);
    } catch (err) {
      console.error('LayerFeaturePanelComponent: failed to compute bbox', err);
    }
  }
}
